// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.
//
// Client/UI state for the alarms home. The alarm *data* is server state owned by
// TanStack Query (see hooks/useConsoleAlarms.ts); this store holds only the view
// preferences — layout and sort — following the existing
// zustand pattern in shellStore.ts. Kept separate from shellStore so alarm-view
// churn doesn't re-render the whole shell.

import { create } from 'zustand';
import { persist } from 'zustand/middleware';

/** Card presentation the list renders in. */
export type AlarmLayout = 'tiles' | 'list';

/** Sort order applied client-side to the fetched alarm rows. */
export type AlarmSort = 'severity' | 'newest' | 'oldest';

export interface AlarmsViewState {
  layout: AlarmLayout;
  sort: AlarmSort;

  setLayout: (_layout: AlarmLayout) => void;
  setSort: (_sort: AlarmSort) => void;
}

export const useAlarmsViewStore = create<AlarmsViewState>()(
  persist(
    set => ({
      layout: 'tiles',
      sort: 'severity',

      setLayout: layout => set({ layout }),
      setSort: sort => set({ sort }),
    }),
    {
      name: 'helios_alarms_view',
      version: 1,
      migrate: (persisted, version) => {
        const state = (persisted ?? {}) as Partial<AlarmsViewState>;
        if (version < 1) {
          const { sort: _dropped, ...rest } = state;
          return rest as AlarmsViewState;
        }
        return state as AlarmsViewState;
      },
      partialize: state => ({ layout: state.layout, sort: state.sort }),
    }
  )
);
